import { supabase } from '@/integrations/supabase/client';

export interface UserProfile {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  telefone?: string;
  role: string;
  status: string;
  tenant_id?: number;
  tenant?: {
    id: number;
    nome: string;
  } | null;
  created_at: string;
  updated_at: string;
  avatar?: string;
}

export interface TenantSettings {
  id: number;
  tenant_id: number;
  porcentagem_comissao: number;
  documentos_obrigatorios: string[];
  email_notificacoes: string;
  dias_vencimento_padrao: number;
  logo_url: string | null;
  cor_primaria: string;
  cor_secundaria: string;
  usa_assinatura_digital: boolean;
  texto_contrato_padrao: string | null;
  dia_inicio_producao?: number;
  created_at: string;
  updated_at: string;
}

export interface UpdateTenantSettingsDto {
  porcentagem_comissao?: number;
  documentos_obrigatorios?: string[];
  email_notificacoes?: string;
  dias_vencimento_padrao?: number;
  logo_url?: string;
  cor_primaria?: string;
  cor_secundaria?: string;
  usa_assinatura_digital?: boolean;
  texto_contrato_padrao?: string;
  dia_inicio_producao?: number;
}

export class ConfiguracoesSupabaseService {
  async getCurrentUser(): Promise<UserProfile> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();

      if (error || !user) {
        throw new Error('Usuário não autenticado');
      }

      // Buscar dados do perfil do usuário com informações do tenant
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          tenants(
            id,
            nome
          )
        `)
        .eq('id', user.id)
        .single();

      // Buscar telefone do consultor
      const { data: consultor } = await supabase
        .from('consultores')
        .select('telefone')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) {
        console.warn('Erro ao buscar perfil:', profileError);
        // Retornar dados básicos do usuário autenticado
        return {
          id: parseInt(user.id),
          email: user.email || '',
          first_name: user.user_metadata?.first_name || '',
          last_name: user.user_metadata?.last_name || '',
          telefone: consultor?.telefone || undefined,
          role: user.user_metadata?.role || 'user',
          status: 'active',
          created_at: user.created_at || new Date().toISOString(),
          updated_at: user.updated_at || new Date().toISOString(),
          avatar: user.user_metadata?.avatar || undefined
        };
      }

      return {
        id: parseInt(profile.id),
        email: profile.email || user.email || '',
        first_name: profile.first_name || user.user_metadata?.first_name || '',
        last_name: profile.last_name || user.user_metadata?.last_name || '',
        telefone: consultor?.telefone || undefined,
        role: profile.role || 'user',
        status: profile.status || 'active',
        tenant_id: profile.tenant_id,
        tenant: profile.tenants ? {
          id: profile.tenants.id,
          nome: profile.tenants.nome
        } : null,
        created_at: profile.created_at || user.created_at || new Date().toISOString(),
        updated_at: profile.updated_at || user.updated_at || new Date().toISOString(),
        avatar: profile.avatar || user.user_metadata?.avatar || undefined
      };
    } catch (error) {
      console.error('Erro no ConfiguracoesSupabaseService.getCurrentUser:', error);
      throw error;
    }
  }

  async updateCurrentUser(data: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        throw new Error('Usuário não autenticado');
      }

      // Atualizar dados do perfil
      const updateData = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        updated_at: new Date().toISOString()
      };

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select(`
          *,
          tenants(
            id,
            nome
          )
        `)
        .single();

      // Atualizar telefone do consultor se fornecido
      if (data.telefone !== undefined) {
        await supabase
          .from('consultores')
          .update({ telefone: data.telefone, updated_at: new Date() })
          .eq('user_id', user.id);
      }

      if (profileError) {
        console.warn('Erro ao atualizar perfil:', profileError);
        // Se não conseguir atualizar o perfil, retornar dados atualizados do usuário
        return {
          id: parseInt(user.id),
          email: data.email || user.email || '',
          first_name: data.first_name || user.user_metadata?.first_name || '',
          last_name: data.last_name || user.user_metadata?.last_name || '',
          telefone: data.telefone,
          role: user.user_metadata?.role || 'user',
          status: 'active',
          created_at: user.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          avatar: data.avatar || user.user_metadata?.avatar || undefined
        };
      }

      return {
        id: parseInt(profile.id),
        email: profile.email || user.email || '',
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        telefone: data.telefone,
        role: profile.role || 'user',
        status: profile.status || 'active',
        tenant_id: profile.tenant_id,
        tenant: profile.tenants ? {
          id: profile.tenants.id,
          nome: profile.tenants.nome
        } : null,
        created_at: profile.created_at || user.created_at || new Date().toISOString(),
        updated_at: profile.updated_at || new Date().toISOString(),
        avatar: profile.avatar || undefined
      };
    } catch (error) {
      console.error('Erro no ConfiguracoesSupabaseService.updateCurrentUser:', error);
      throw error;
    }
  }

  async updateAvatar(file: File): Promise<{ avatar: string }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        throw new Error('Usuário não autenticado');
      }

      // Upload do arquivo para o storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Obter URL pública do arquivo
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Atualizar perfil com nova URL do avatar
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          avatar: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        console.warn('Erro ao atualizar avatar no perfil:', updateError);
      }

      return { avatar: publicUrl };
    } catch (error) {
      console.error('Erro no ConfiguracoesSupabaseService.updateAvatar:', error);
      throw error;
    }
  }

  async updatePassword(data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<void> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Erro no ConfiguracoesSupabaseService.updatePassword:', error);
      throw error;
    }
  }

  async terminateSession(sessionId: string): Promise<void> {
    try {
      // Supabase não permite encerrar sessões específicas via API
      // Apenas todas as sessões exceto a atual
      const { error } = await supabase.auth.signOut({ scope: 'others' });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Erro no ConfiguracoesSupabaseService.terminateSession:', error);
      throw error;
    }
  }

  async toggleTwoFactor(enabled: boolean): Promise<void> {
    try {
      // Supabase não suporta 2FA via API diretamente
      // Esta funcionalidade precisaria ser implementada separadamente
      console.warn('2FA não implementado para Supabase ainda');
    } catch (error) {
      console.error('Erro no ConfiguracoesSupabaseService.toggleTwoFactor:', error);
      throw error;
    }
  }

  async updateTenantSettings(tenantId: number, settings: Partial<UpdateTenantSettingsDto>): Promise<TenantSettings> {
    try {
      const updateData = {
        ...settings,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('tenant_settings')
        .update(updateData)
        .eq('tenant_id', tenantId)
        .select('*')
        .maybeSingle();

      if (error) {
        throw error;
      }

      // UPDATE sem linha não é erro: cria as configurações iniciais do tenant.
      if (!data) {
        const { data: newSettings, error: createError } = await supabase
          .from('tenant_settings')
          .insert([{
            tenant_id: tenantId,
            ...settings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select('*')
          .single();

        if (createError) {
          throw createError;
        }

        return newSettings;
      }

      return data;
    } catch (error) {
      console.error('Erro no ConfiguracoesSupabaseService.updateTenantSettings:', error);
      throw error;
    }
  }

  async getTenantSettings(tenantId: number): Promise<TenantSettings | null> {
    try {
      const { data, error } = await supabase
        .from('tenant_settings')
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro no ConfiguracoesSupabaseService.getTenantSettings:', error);
      throw error;
    }
  }
}

export const configuracoesSupabaseService = new ConfiguracoesSupabaseService();


